// API configuration
// Uses VITE_API_URL at build time, falls back to relative path for local Docker Compose
// VITE_API_URL should be the backend base URL (e.g., https://backend.azurecontainerapps.io)
// We append /api since all backend routes are mounted under /api
// For local development with nginx proxy, falls back to empty string (relative paths)
const BACKEND_URL = import.meta.env.VITE_API_URL || '';
export const API_BASE_URL = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

console.log('=== API CONFIGURATION ===');
console.log('BACKEND_URL:', BACKEND_URL || '(relative - local mode)');
console.log('API_BASE_URL:', API_BASE_URL);
console.log('=========================');

/**
 * Get the authentication token from localStorage
 */
function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError && error.message.includes('fetch');
}

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on authentication errors or client errors (4xx)
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        throw error;
      }
      
      // If this is the last retry, throw the error
      if (i === maxRetries - 1) {
        throw error;
      }
      
      // Wait with exponential backoff
      const delay = baseDelay * Math.pow(2, i);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

/**
 * Get or generate session ID
 */
function getSessionId(): string {
  let sessionId = localStorage.getItem('session_id');
  if (!sessionId) {
    // Generate a random session ID
    sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('session_id', sessionId);
  }
  return sessionId;
}

/**
 * Get CSRF token from localStorage or fetch a new one
 */
async function getCSRFToken(): Promise<string> {
  const sessionId = getSessionId();
  
  // Check if we have a cached token
  const cachedToken = localStorage.getItem('csrf_token');
  const tokenExpiry = localStorage.getItem('csrf_token_expiry');
  
  if (cachedToken && tokenExpiry && Date.now() < parseInt(tokenExpiry)) {
    return cachedToken;
  }
  
  // Fetch a new token from the backend
  // Use BACKEND_URL directly since /health is not under /api
  try {
    const response = await fetch(`${BACKEND_URL}/health`, {
      method: 'GET',
      headers: {
        'X-Session-Id': sessionId,
      },
    });
    
    const csrfToken = response.headers.get('X-CSRF-Token');
    if (csrfToken) {
      // Cache token for 50 minutes (backend expires in 1 hour)
      localStorage.setItem('csrf_token', csrfToken);
      localStorage.setItem('csrf_token_expiry', (Date.now() + 50 * 60 * 1000).toString());
      return csrfToken;
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
  
  return '';
}

export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { skipRetry?: boolean }
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add any custom headers from options
  if (options?.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        headers[key] = value;
      }
    });
  }

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add CSRF token for non-GET requests
  if (options?.method && !['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())) {
    const sessionId = getSessionId();
    const csrfToken = await getCSRFToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
      headers['X-Session-Id'] = sessionId;
    }
  }
  
  const makeRequest = async () => {
    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          message: 'An error occurred',
          code: 'UNKNOWN_ERROR'
        }));
        
        // Handle authentication errors - redirect to login
        if (response.status === 401 || response.status === 403) {
          // Clear token on auth error
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth_token');
            // Redirect to login if not already there
            if (!window.location.pathname.includes('/admin/login')) {
              window.location.href = '/admin/login';
            }
          }
        }
        
        // Handle nested error structure from backend
        const errorMessage = errorData.error?.message || errorData.message || `HTTP ${response.status}`;
        const errorCode = errorData.error?.code || errorData.code;
        
        throw new ApiError(
          errorMessage,
          response.status,
          errorCode
        );
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null as T;
      }

      return response.json();
    } catch (error) {
      // Re-throw ApiError as-is
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Handle network errors
      if (error instanceof TypeError) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw error;
    }
  };
  
  // Use retry logic unless explicitly disabled
  if (options?.skipRetry) {
    return makeRequest();
  }
  
  return retryWithBackoff(makeRequest);
}


/**
 * Build a full API URL for raw fetch calls (like file uploads)
 * Use this when you can't use fetchApi (e.g., FormData uploads)
 * 
 * Endpoints should NOT include /api prefix - it's already in API_BASE_URL
 * ✅ Correct: buildApiUrl('/admin/attendees/import')
 * ❌ Wrong: buildApiUrl('/api/admin/attendees/import')
 */
export function buildApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}

/**
 * Get headers for raw fetch calls with auth and CSRF
 * Use this for file uploads or other cases where you need manual fetch
 */
export function getApiHeaders(includeContentType = true): Record<string, string> {
  const headers: Record<string, string> = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

/**
 * Get CSRF headers for POST/PUT/DELETE requests
 */
export function getCSRFHeaders(): Record<string, string> {
  const sessionId = localStorage.getItem('session_id') || '';
  const csrfToken = localStorage.getItem('csrf_token') || '';
  
  return {
    'X-CSRF-Token': csrfToken,
    'X-Session-Id': sessionId,
  };
}
